<template>
  <div :class="classNames">
    <span class="nr-tag--text">{{ label }}</span>
    <NrIconButton
      v-if="icon && clickable && !badge"
      variant="minimal-neutral"
      :icon="{ name: icon, type: iconType }"
      size="small"
      :aria-label="ariaLabel"
      @click="$emit('on-click')"
    />
    <NrIcon
      v-if="icon && !clickable && !badge"
      :name="icon"
      :iconType="iconType"
    />
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue'
import NrIconButton from '../NrIconButton/nr-icon-button.vue'
import NrIcon from '../NrIcon/NrIcon.vue'

export default {
  name: 'NrTag',
  props: {
    label: {
      type: String,
      required: true
    },
    size: {
      type: String,
      default: 'normal'
    },
    tagColor: {
      type: String,
      default: 'black'
    },
    iconColor: {
      type: String,
      default: 'black'
    },
    icon: {
      type: String,
      default: ''
    },
    clickable: {
      type: Boolean,
      default: false
    },
    iconType: {
      type: String as PropType<'regular' | 'solid'>,
      default: 'regular'
    },
    iconPosition: {
      type: String,
      default: 'right'
    },
    ariaLabel: {
      type: String,
      default: 'toggle'
    },
    badge: {
      type: Boolean,
      default: false
    }
  },
  components: {
    NrIcon,
    NrIconButton
  },
  computed: {
    classNames() {
      let tagClasses = [
        'nr-tag',
        `nr-tag--${this.size}`,
        `nr-tag--badge-${this.badge}`,
        `nr-tag--${this.tagColor}`
      ]
      if (this.icon) {
        tagClasses = [
          ...tagClasses,
          `nr-tag--icon-position-${this.iconPosition}`,
          `nr-tag--icon-${!!this.icon}`,
          `nr-tag--icon-clickable-${this.clickable}`
        ]
      }
      return tagClasses
    }
  }
}
</script>
<style lang="scss">
@import '../../styles/tokens/tokens.scss';
.nr-tag {
  background-color: $color-purple-110;
  color: $color-white;
  border-radius: $border-radius-medium;
  display: inline-flex;
  font-weight: $font-weight-semibold;
  &.nr-tag--small {
    font-size: $font-size-1;
    height: $font-lineheight-2;
    padding: $spacing-none $spacing-6;
    align-items: center;
    .nr-icon {
      font-size: $font-size-1;
      width: 12px;
    }
    &.nr-tag--icon-clickable-false {
      .nr-icon {
        margin: $spacing-6 $spacing-6;
      }
    }
    &.nr-tag--icon-position-right {
      padding-right: $spacing-none;
      flex-direction: row;
    }
    &.nr-tag--icon-position-left {
      flex-direction: row-reverse;
      padding-left: $spacing-none;
    }
    &.nr-tag--badge-true {
      border-radius: $spacing-12;
      padding: $spacing-6;
    }
  }

  &.nr-tag--normal {
    font-size: $font-size-3;
    height: $font-lineheight-3;
    padding: $spacing-none $spacing-6;
    &.nr-tag--icon-clickable-false {
      .nr-icon {
        margin: $spacing-6 $spacing-6;
        font-size: $font-size-3;
        width: 14px;
      }
    }
    :not(.nr-tag--icon-position-left) {
      .nr-tag--text {
        padding: $spacing-none $spacing-8;
      }
    }
    .nr-icon-button {
      .nr-icon {
        font-size: $font-size-3;
        width: 14px;
      }
    }
    &.nr-tag--icon-position-right {
      flex-direction: row;
      padding-right: $spacing-none;
    }
    &.nr-tag--icon-position-left {
      flex-direction: row-reverse;
      padding-left: $spacing-none;
    }
    &.nr-tag--badge-true {
      border-radius: $spacing-12;
      padding-right: $spacing-6;
      padding-left: $spacing-6;
    }
  }

  &.nr-tag--large {
    font-size: $font-size-5;
    height: $font-lineheight-5;
    padding: $spacing-none $spacing-8;
    &.nr-tag--icon-clickable-false {
      .nr-icon {
        margin: $spacing-8 $spacing-4;
        font-size: $font-size-4;
      }
    }
    :not(.nr-tag--icon-position-left) {
      .nr-tag--text {
        padding: $spacing-none $spacing-8;
      }
    }
    &.nr-tag--icon-position-right {
      flex-direction: row;
    }
    &.nr-tag--icon-position-left {
      flex-direction: row-reverse;
    }
    .nr-icon-button {
      .nr-icon {
        font-size: $font-size-4;
      }
    }
    &.nr-tag--badge-true {
      border-radius: $spacing-12;
    }
  }

  &.nr-tag--yellow {
    background-color: $color-yellow-100;
    color: $color-purple-110;
  }

  &.nr-tag--green {
    background-color: $color-green-100;
    .nr-button {
      color: $color-white;
      &:hover,
      &:focus {
        color: $color-gray-50;
      }
    }
  }

  &.nr-tag--blue {
    background-color: $color-blue-100;
    .nr-button {
      color: $color-white;
      &:hover,
      &:focus {
        color: $color-gray-50;
      }
    }
    color: $color-white;
  }

  &.nr-tag--purple {
    background-color: $color-purple-100;
    .nr-button {
      color: $color-white;
      &:hover,
      &:focus {
        color: $color-gray-50;
      }
    }
  }

  &.nr-tag--gray {
    background-color: $color-gray-40;
    color: $color-purple-110;
  }

  &.nr-tag--red {
    background-color: $color-red-100;
    .nr-button {
      color: $color-white;
      &:hover,
      &:focus {
        color: $color-gray-50;
      }
    }
  }

  &.nr-tag--black {
    background-color: $color-black;
    .nr-button {
      color: $color-white;
      &:hover,
      &:focus {
        color: $color-gray-50;
      }
    }
  }
}
</style>
